import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, EyeOff, ArrowLeft, FileText, Calendar, Search, ExternalLink, BarChart3, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchPosts();
    fetchAnalytics();
  }, [navigate]);

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/blog/posts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch posts');
      const data = await response.json();
      setPosts(data);
    } catch (err) {
      // Error handled by UI
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/blog/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (err) {
      // Analytics fetch non-critical
    }
  };

  const handleDelete = async (postId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/blog/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete post');
      setPosts(posts.filter(p => p.id !== postId));
      setDeleteConfirm(null);
    } catch (err) {
      // Error handled by UI
      alert('Failed to delete post');
    }
  };

  const togglePublish = async (post) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/blog/posts/${post.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_published: !post.is_published })
      });
      if (!response.ok) throw new Error('Failed to update post');
      fetchPosts(); // Refresh the list
    } catch (err) {
      // Error handled by UI
      alert('Failed to update post');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white" data-testid="admin-blog-page">
      {/* Header */}
      <header className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/admin/dashboard"
                className="text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="font-heading text-xl font-bold">Blog Management</h1>
                <p className="text-sm text-slate-500">Create and manage blog posts</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/admin/blog/new')}
              className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full px-6 py-2 font-bold shadow-[0_0_20px_rgba(16,185,129,0.3)]"
              data-testid="create-post-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              New Post
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              type="text"
              placeholder="Search posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 rounded-xl"
              data-testid="search-posts-input"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <div className="backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <p className="text-slate-500 text-sm">Total Posts</p>
            <p className="text-2xl font-bold text-white">{posts.length}</p>
          </div>
          <div className="backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <p className="text-slate-500 text-sm">Published</p>
            <p className="text-2xl font-bold text-emerald-400">{posts.filter(p => p.is_published).length}</p>
          </div>
          <div className="backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <p className="text-slate-500 text-sm">Total Views</p>
            <p className="text-2xl font-bold text-cyan-400">{analytics?.total_views || 0}</p>
          </div>
          <div className="backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <p className="text-slate-500 text-sm">View Blog</p>
            <a 
              href="/blog" 
              target="_blank" 
              className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1 mt-1"
            >
              Open <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* Analytics Toggle */}
        <div className="mb-8">
          <button
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <BarChart3 className="h-4 w-4" />
            {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
          </button>
          
          {showAnalytics && analytics && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              {/* Top Posts */}
              <div className="backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-400" />
                  Top Posts by Views
                </h3>
                <div className="space-y-2">
                  {analytics.top_posts?.slice(0, 5).map((post, i) => (
                    <div key={i} className="flex justify-between items-center text-sm">
                      <span className="text-slate-300 truncate max-w-[200px]">{post.title}</span>
                      <span className="text-cyan-400 font-mono">{post.view_count || 0}</span>
                    </div>
                  ))}
                  {(!analytics.top_posts || analytics.top_posts.length === 0) && (
                    <p className="text-slate-500 text-sm">No views yet</p>
                  )}
                </div>
              </div>

              {/* UTM Sources */}
              <div className="backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-cyan-400" />
                  Traffic Sources (30 days)
                </h3>
                <div className="space-y-2">
                  {analytics.utm_sources?.map((source, i) => (
                    <div key={i} className="flex justify-between items-center text-sm">
                      <span className="text-slate-300">{source.source || 'Direct'}</span>
                      <span className="text-emerald-400 font-mono">{source.views}</span>
                    </div>
                  ))}
                  {(!analytics.utm_sources || analytics.utm_sources.length === 0) && (
                    <p className="text-slate-500 text-sm">No tracked sources yet</p>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Add ?utm_source=sms to track SMS links
                </p>
              </div>
            </motion.div>
          )}
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
          </div>
        ) : filteredPosts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-800/50 border border-slate-700 flex items-center justify-center">
              <FileText className="h-8 w-8 text-slate-500" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">
              {searchTerm ? 'No posts found' : 'No blog posts yet'}
            </h3>
            <p className="text-slate-400 mb-6">
              {searchTerm ? 'Try a different search term' : 'Create your first blog post to get started'}
            </p>
            {!searchTerm && (
              <Button
                onClick={() => navigate('/admin/blog/new')}
                className="bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-full px-6"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create First Post
              </Button>
            )}
          </motion.div>
        ) : (
          <div className="space-y-4">
            {filteredPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-xl p-4 sm:p-6 hover:border-slate-700 transition-all"
                data-testid={`blog-post-item-${post.slug}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-white truncate">{post.title}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        post.is_published 
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                          : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                      }`}>
                        {post.is_published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      <span className="font-mono text-xs bg-slate-800 px-2 py-1 rounded">/{post.slug}</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {post.is_published ? formatDate(post.published_at) : `Created ${formatDate(post.created_at)}`}
                      </span>
                      {post.view_count > 0 && (
                        <span className="flex items-center gap-1 text-cyan-400">
                          <Eye className="h-3 w-3" />
                          {post.view_count} views
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => togglePublish(post)}
                      variant="ghost"
                      size="sm"
                      className={`rounded-full ${post.is_published ? 'text-amber-400 hover:bg-amber-500/10' : 'text-emerald-400 hover:bg-emerald-500/10'}`}
                      title={post.is_published ? 'Unpublish' : 'Publish'}
                    >
                      {post.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    {post.is_published && (
                      <a
                        href={`/blog/${post.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-full transition-colors"
                        title="View post"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                    <Button
                      onClick={() => navigate(`/admin/blog/edit/${post.id}`)}
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-white hover:bg-slate-800 rounded-full"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {deleteConfirm === post.id ? (
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handleDelete(post.id)}
                          size="sm"
                          className="bg-red-500 hover:bg-red-600 text-white rounded-full px-3 text-xs"
                        >
                          Confirm
                        </Button>
                        <Button
                          onClick={() => setDeleteConfirm(null)}
                          variant="ghost"
                          size="sm"
                          className="text-slate-400 rounded-full px-3 text-xs"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => setDeleteConfirm(post.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-full"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBlog;
